import React, { useCallback, useEffect, useState } from 'react';
import './WalletPage.css';
import { useLanguage } from './i18n/LanguageContext';
import { apiRequest, parseResponse, getLoggedInUserId } from './utils/api';
import { maskCardNumber, normalizeSavedCard } from './utils/cards';

const RAZORPAY_SCRIPT_SRC = 'https://checkout.razorpay.com/v1/checkout.js';

const loadRazorpayScript = () => new Promise((resolve, reject) => {
  if (window.Razorpay) {
    resolve();
    return;
  }

  const existing = document.querySelector(`script[src="${RAZORPAY_SCRIPT_SRC}"]`);
  const attach = (script) => {
    const timeoutId = window.setTimeout(() => {
      if (window.Razorpay) {
        resolve();
      } else {
        reject(new Error('Failed to load Razorpay'));
      }
    }, 10000);
    script.addEventListener('load', () => {
      window.clearTimeout(timeoutId);
      resolve();
    });
    script.addEventListener('error', () => {
      window.clearTimeout(timeoutId);
      reject(new Error('Failed to load Razorpay'));
    });
  };

  if (existing) {
    attach(existing);
    return;
  }

  const script = document.createElement('script');
  script.src = RAZORPAY_SCRIPT_SRC;
  script.async = true;
  attach(script);
  document.body.appendChild(script);
});

const getCardIcon = (cardType) => {
  const type = String(cardType || '').toLowerCase();
  if (type.includes('visa')) return 'VISA';
  if (type.includes('master')) return 'MASTERCARD';
  if (type.includes('amex') || type.includes('american')) return 'AMEX';
  if (type.includes('rupay')) return 'RUPAY';
  return (cardType || 'CARD').toUpperCase();
};

const formatExpiry = (card) => {
  if (!card.expiryMonth || !card.expiryYear) {
    return '--/--';
  }
  return `${String(card.expiryMonth).padStart(2, '0')}/${String(card.expiryYear).slice(-2)}`;
};

const readOrder = (orderData) => {
  let order = orderData?.data || orderData;
  if (typeof order === 'string') {
    try {
      order = JSON.parse(order);
    } catch (error) {
      return null;
    }
  }
  return order && typeof order === 'object' ? order : null;
};

const WalletPage = ({ onBack, onRequireLogin }) => {
  const { t } = useLanguage();
  const [savedCards, setSavedCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState(null);

  const fetchSavedCards = useCallback(async ({ silent } = {}) => {
    const userId = getLoggedInUserId();
    if (!userId) {
      setSavedCards([]);
      setLoading(false);
      return;
    }

    try {
      if (!silent) {
        setLoading(true);
      }
      const response = await apiRequest(`/payments/saved-cards/${userId}`, { method: 'GET' });
      const data = await parseResponse(response);
      if (!response.ok) {
        setSavedCards([]);
        return;
      }
      const cards = Array.isArray(data) ? data : (data?.data || []);
      setSavedCards((Array.isArray(cards) ? cards : []).map(normalizeSavedCard).filter((card) => card.id));
    } catch (err) {
      console.warn('Unable to load saved cards', err);
      setSavedCards([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSavedCards();
  }, [fetchSavedCards]);

  const handleDeleteCard = async (cardId) => {
    if (!window.confirm(t('Are you sure you want to delete this card?'))) {
      return;
    }

    try {
      const response = await apiRequest(`/payments/saved-cards/${cardId}`, { method: 'DELETE' });
      const data = await parseResponse(response);
      if (!response.ok || data?.status === false) {
        throw new Error(data?.message || t('Failed to delete card'));
      }
      setSavedCards((cards) => cards.filter((card) => card.id !== cardId));
    } catch (err) {
      setError(err.message || t('Failed to delete card'));
    }
  };

  const handleAddCard = async () => {
    if (adding) {
      return;
    }

    const userId = getLoggedInUserId();
    if (!userId) {
      if (typeof onRequireLogin === 'function') {
        onRequireLogin();
        return;
      }
      setError(t('Please complete guest verification first.'));
      return;
    }

    try {
      setAdding(true);
      setError(null);
      await loadRazorpayScript();

      const guestData = JSON.parse(localStorage.getItem('guestData') || '{}');
      const userData = guestData?.userData?.data || guestData?.userData || {};
      const contact = String(userData.mobileNumber || guestData.mobileNumber || '').replace(/\D/g, '');

      const orderResponse = await apiRequest('/payments/create-order', {
        method: 'POST',
        body: JSON.stringify({
          amount: 1,
          currency: 'INR',
          receipt: `save_card_${userId}_${Date.now()}`
        })
      });
      const orderData = await parseResponse(orderResponse);
      if (!orderResponse.ok || orderData?.status === false) {
        throw new Error(orderData?.message || t('Failed to add card'));
      }
      const order = readOrder(orderData);
      const orderId = order?.id || order?.order_id;
      if (!orderId) {
        throw new Error(t('Failed to add card'));
      }

      const razorpayKey = order.key
        || order.razorpayKey
        || order.razorpay_key_id
        || order.key_id
        || process.env.REACT_APP_RAZORPAY_KEY_ID
        || 'rzp_test_SDiRM8tiLqJl67';
      if (!window.Razorpay) {
        throw new Error(t('Failed to add card'));
      }

      const backendAmount = Number(order.amount);
      const razorpayAmount = backendAmount > 0 ? backendAmount : 100;
      const finishCheckout = () => {
        document.body.classList.remove('razorpay-open');
        setAdding(false);
      };

      document.body.classList.add('razorpay-open');
      const checkout = new window.Razorpay({
        key: razorpayKey,
        amount: razorpayAmount,
        currency: order.currency || 'INR',
        name: 'Moviq',
        description: t('Add Payment Method'),
        order_id: orderId,
        prefill: {
          name: userData.fullName || userData.name || guestData.fullName || '',
          email: userData.email || guestData.email || '',
          ...(contact.length >= 10 ? { contact } : {}),
          method: 'card'
        },
        theme: { color: '#764CA2' },
        save: 1,
        save_card: 1,
        handler: async (response) => {
          try {
            const verifyResponse = await apiRequest('/payments/verify', {
              method: 'POST',
              body: JSON.stringify({
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                saveCard: true,
                userId
              })
            });
            const verifyData = await parseResponse(verifyResponse);
            if (!verifyResponse.ok || !(verifyData?.status || verifyData?.success)) {
              throw new Error(verifyData?.message || t('Failed to add card'));
            }
            await fetchSavedCards({ silent: true });
          } catch (verifyError) {
            setError(verifyError.message || t('Failed to add card'));
          } finally {
            finishCheckout();
          }
        },
        modal: {
          ondismiss: finishCheckout
        }
      });
      checkout.on('payment.failed', () => {
        finishCheckout();
        setError(t('Failed to add card'));
      });
      checkout.open();
    } catch (err) {
      document.body.classList.remove('razorpay-open');
      setAdding(false);
      setError(err.message || t('Failed to add card'));
    }
  };

  const addButton = (extraClass) => (
    <button
      type="button"
      className={`add-payment-btn ${extraClass}`}
      onClick={handleAddCard}
      disabled={adding}
    >
      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
      <span>{adding ? t('Processing...') : t('Add Payment Method')}</span>
    </button>
  );

  return (
    <div className="wallet-page">
      <div className="wallet-header">
        <button type="button" className="back-circle-btn" onClick={onBack} aria-label={t('Go back')}>
          <img
            src={process.env.PUBLIC_URL + '/images/back_button.svg'}
            alt=""
          />
        </button>
        <div className="wallet-heading">
          <h1 className="wallet-title">{t('Payment Methods')}</h1>
          <p className="wallet-subtitle">{t('Manage payment methods')}</p>
        </div>
      </div>

      <div className="wallet-content">
        <div className="payment-content">
          <div className="saved-cards-visual">
            {loading && (
              <div className="saved-payment-empty">
                <p>{t('Loading saved cards...')}</p>
              </div>
            )}

            {!loading && savedCards.length === 0 && (
              <div className="saved-payment-empty">
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="2" y="5" width="20" height="14" rx="2" stroke="currentColor" strokeWidth="2"/>
                  <path d="M2 10H22" stroke="currentColor" strokeWidth="2"/>
                </svg>
                <p>{t('No saved cards')}</p>
                <p className="empty-subtitle">{t('Add a card to make payments faster')}</p>
              </div>
            )}

            {!loading && savedCards.map((card) => (
              <div className="payment-card" key={`visual-${card.id}`}>
                <div className="card-top">
                  <div className="card-chip"></div>
                  <span className="card-type">{getCardIcon(card.cardType)}</span>
                </div>
                <div className="card-number">
                  <span className="card-dots">•••• •••• ••••</span>
                  <span className="card-last4">{card.last4}</span>
                </div>
                <div className="card-bottom">
                  <div className="card-info-group">
                    <span className="card-label">{t('CARD HOLDER')}</span>
                    <span className="card-value">{card.name}</span>
                  </div>
                  <div className="card-info-group text-right">
                    <span className="card-label">{t('EXPIRES')}</span>
                    <span className="card-value">{formatExpiry(card)}</span>
                  </div>
                </div>
                <div className="card-decoration"></div>
              </div>
            ))}
          </div>
          {addButton('tablet-veiw')}
        </div>

        <div className="wallet-method-list">
          {savedCards.map((card) => (
            <div className="wallet-method-item" key={`row-${card.id}`}>
              <div className="method-left">
                <div className="method-icon-bg" aria-hidden="true">
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="2" y="5" width="20" height="14" rx="2" stroke="currentColor" strokeWidth="2"/>
                    <path d="M2 10H22" stroke="currentColor" strokeWidth="2"/>
                  </svg>
                </div>
                <div className="method-details">
                  <span className="method-name">{getCardIcon(card.cardType)}</span>
                  <span className="method-meta">{maskCardNumber(card.last4)}</span>
                </div>
              </div>
              <button
                type="button"
                className="method-delete-btn"
                onClick={() => handleDeleteCard(card.id)}
                aria-label={t('Delete card')}
              >
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M3 6H5H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
          ))}

          <div className="wallet-method-item">
            <div className="method-left">
              <div className="method-icon-bg" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="3" y="6" width="18" height="12" rx="2" stroke="currentColor" strokeWidth="2"/>
                  <path d="M3 10H21" stroke="currentColor" strokeWidth="2"/>
                  <path d="M7 15H9" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </div>
              <div className="method-details">
                <span className="method-name">{t('Cash')}</span>
                <span className="method-meta">{t('Pay directly to driver after trip ends.')}</span>
              </div>
            </div>
          </div>
        </div>

        {error && <p className="wallet-add-error">{error}</p>}

        {addButton('moblie-veiw')}
      </div>
    </div>
  );
};

export default WalletPage;
